import { useEffect, useMemo, useRef } from 'react'
import { useThree, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { buildBrdfLut, makeFgLutSampler } from '../hair/shading'
import {
  makeSHDiffuseSampler,
  makeSHSpecularSampler,
  lightProbeToSH27,
  INV_PI,
} from '../lib/envSamplers'
import { shadeMeshEnvmap } from '../lib/meshEnvShade'
import { useProbeRig } from '../hooks/useProbeRig'

/**
 * MaterialBallProbe — the env-debug oracle. A keyboard-driven rig of 3js mesh
 * balls shaded by the BecomingLit equation (shadeGaussiansEnvmap), with BOTH
 * diffuseEnv and specularEnv sampling the ENG-561 baked light-probe VOLUME (the
 * SH grid → THREE.LightProbe). No reflection-capture images, no runtime cube:
 * the SH volume is the single env source, and the specular sampler factors in
 * roughness by attenuating the higher SH bands.
 *
 * A chrome mirror (left) + a dielectric roughness sweep — what to judge.
 *
 * The rig owns window.__AVATAR_PROBE_POS__ (useProbeRig): arrow keys / PageUp-
 * Down drive it and LightProbeVolume re-samples the SH grid at that point, so
 * every ball relights as you move through the room.
 *
 * Live tunables: window.__ENV_DEBUG_DIFFUSE_SCALE__ (×, default 1),
 *                window.__ENV_DEBUG_SPEC_SCALE__ (×, default 1).
 */

const ALBEDO = [0.8, 0.8, 0.8]
// Left→right: a chrome mirror (low albedo → specular dominates) then a dielectric
// roughness sweep. ALL shaded from the SH volume — the chrome ball reflects the
// (band-limited) SH radiance, so it's a soft mirror, not a sharp one.
const BALLS = [
  { albedo: [0.03, 0.03, 0.03], roughness: 0.02 }, // chrome
  { albedo: ALBEDO, roughness: 0.05 },
  { albedo: ALBEDO, roughness: 0.2 },
  { albedo: ALBEDO, roughness: 0.45 },
  { albedo: ALBEDO, roughness: 0.7 },
  { albedo: ALBEDO, roughness: 1.0 },
]
const BALL_RADIUS = 0.13
const BALL_GAP = 0.31
const RESHADE_MS = 80 // re-shade throttle (LightProbe SH updates per frame)

export default function MaterialBallProbe({ position = [0, 1.4, 0] }) {
  const camera = useThree(s => s.camera)
  const { groupRef } = useProbeRig(position)

  // FG LUT (Karis split-sum) — built once; the env_specular (A+B) term.
  const fgLut = useMemo(() => makeFgLutSampler(buildBrdfLut(128, 512)), [])

  // Single RAW SH snapshot of the LightProbe, shared by both samplers. Diffuse
  // folds 1/π (Lambertian) via its scale ref; specular keeps raw radiance.
  const sh27 = useRef(new Float32Array(27))
  const diffuseScale = useRef({ value: INV_PI })
  const specScale = useRef({ value: 1 })
  const diffuseEnv = useMemo(() => makeSHDiffuseSampler(sh27.current, diffuseScale.current), [])
  const specularEnv = useMemo(() => makeSHSpecularSampler(sh27.current, specScale.current), [])

  // One geometry per ball (each owns its written `color` attribute); shared mat.
  const geoms = useMemo(
    () => BALLS.map(() => new THREE.SphereGeometry(BALL_RADIUS, 48, 48)),
    [],
  )
  const ballMat = useMemo(
    () => new THREE.MeshBasicMaterial({ vertexColors: true, toneMapped: true }),
    [],
  )
  const ballRefs = useRef([])

  const scratch = useRef({ camPos: new THREE.Vector3(), pos: new THREE.Vector3(), lastShade: 0 })

  useEffect(() => () => {
    geoms.forEach(g => g.dispose())
    ballMat.dispose()
  }, [geoms, ballMat])

  useFrame(() => {
    const grp = groupRef.current
    if (!grp) return
    const sc = scratch.current
    const now = performance.now()
    if (now - sc.lastShade < RESHADE_MS) return
    sc.lastShade = now

    const probe = (typeof window !== 'undefined' && window.__LIGHT_PROBE__) || null
    if (!probe) return // no grid yet → nothing to shade from

    // Snapshot the live LightProbe SH (the per-frame grid sample at the rig).
    lightProbeToSH27(probe, sh27.current)
    const dS = (typeof window !== 'undefined' && +window.__ENV_DEBUG_DIFFUSE_SCALE__) || 1
    const sS = (typeof window !== 'undefined' && +window.__ENV_DEBUG_SPEC_SCALE__) || 1
    diffuseScale.current.value = INV_PI * dS
    specScale.current.value = sS

    grp.getWorldPosition(sc.pos)
    camera.getWorldPosition(sc.camPos)
    const campos = [sc.camPos.x, sc.camPos.y, sc.camPos.z]
    const report = []
    for (let i = 0; i < ballRefs.current.length; i++) {
      const mesh = ballRefs.current[i]
      if (!mesh) continue
      const spec = BALLS[i]
      shadeMeshEnvmap(mesh, {
        albedo: spec.albedo,
        roughness: spec.roughness,
        specVis: 1,
        campos,
        diffuseEnv,
        specularEnv,
        fgLut,
      })
      const a = mesh.geometry.getAttribute('color')?.array
      if (a) {
        let sum = 0, hi = 0
        for (let k = 0; k < a.length; k++) { sum += a[k]; if (a[k] > hi) hi = a[k] }
        report.push({ roughness: spec.roughness, mean: +(sum / a.length).toFixed(4), max: +hi.toFixed(4) })
      }
    }
    if (typeof window !== 'undefined') {
      window.__ENV_DEBUG__ = { pos: [sc.pos.x, sc.pos.y, sc.pos.z], balls: report }
    }
  })

  // Chrome + becominglit roughness row centered on the rig.
  const n = BALLS.length
  const x0 = -((n - 1) * BALL_GAP) / 2
  return (
    <group ref={groupRef} position={position}>
      {BALLS.map((b, i) => (
        <mesh
          key={i}
          ref={el => { ballRefs.current[i] = el }}
          geometry={geoms[i]}
          material={ballMat}
          position={[x0 + i * BALL_GAP, 0, 0]}
          userData={{ noEnvBake: true }}
        />
      ))}
    </group>
  )
}
